// Base API configuration for StockerAI v3
// Prod redeploy trigger - 2026-09-23

export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

export interface ApiError {
  status: number;
  message: string;
  detail?: string;
  fieldErrors?: Record<string, string | string[]>;
}

interface FetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  headers?: Record<string, string>;
  body?: unknown;
  params?: Record<string, string | number | boolean | string[] | number[]>;
  withAuth?: boolean;
  /** Si se aborta, `fetch` rechaza con `DOMException` (`name === 'AbortError'`). */
  signal?: AbortSignal;
}

export async function fetchApi<T>(endpoint: string, options: FetchOptions = {}): Promise<T> {
  const { method = 'GET', headers: customHeaders = {}, body, params, withAuth = true, signal } = options;

  let url = `${API_BASE_URL}/${endpoint}`;
  if (params && typeof params === 'object') {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach((v) => queryParams.append(key, String(v)));
        } else if (typeof value === 'boolean') {
          queryParams.append(key, value ? 'true' : 'false');
        } else {
          queryParams.append(key, String(value));
        }
      }
    });
    const queryString = queryParams.toString();
    if (queryString) url += `?${queryString}`;
  }

  const headers = new Headers(customHeaders);
  if (!headers.has('Content-Type') && method !== 'GET') {
    headers.set('Content-Type', 'application/json');
  }
  if (withAuth) {
    const token = localStorage.getItem('auth_token');
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }

  const config: RequestInit = { method, headers, signal };
  if (body && typeof body === 'object') {
    config.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(url, config);
    if (response.status === 401 && withAuth) {
      const refreshed = await refreshToken();
      if (refreshed) {
        const token = localStorage.getItem('auth_token');
        if (token) headers.set('Authorization', `Bearer ${token}`);
        return handleResponse<T>(await fetch(url, { ...config, headers }));
      }
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      throw { status: 401, message: 'Session expired. Please login again.' } as ApiError;
    }
    return handleResponse<T>(response);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
    throw {
      status: 0,
      message: 'Network error',
      detail: (error as Error).message,
    } as ApiError;
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.indexOf('application/json') !== -1) {
    const data = await response.json();
    if (!response.ok) {
      const message =
        (typeof data.message === 'string' && data.message) ||
        (typeof data.detail === 'string' && data.detail) ||
        (typeof data.error === 'string' && data.error) ||
        (typeof data.details === 'string' && data.details) ||
        'An error occurred';
      const error: ApiError = { status: response.status, message };
      if (data.non_field_errors) {
        error.detail = Array.isArray(data.non_field_errors) ? data.non_field_errors.join(', ') : data.non_field_errors;
      } else if (typeof data.detail === 'string' && data.detail !== message) {
        error.detail = data.detail;
      } else if (data.errors) {
        error.detail = typeof data.errors === 'string' ? data.errors : JSON.stringify(data.errors);
      } else if (typeof data.details === 'string' && data.details !== message) {
        error.detail = data.details;
      }
      throw error;
    }
    return data as T;
  }
  if (!response.ok) throw { status: response.status, message: 'Server error' } as ApiError;
  return {} as T;
}

async function refreshToken(): Promise<boolean> {
  const ref = localStorage.getItem('refresh_token');
  if (!ref) return false;
  try {
    const res = await fetch(`${API_BASE_URL}/auth/token/refresh/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh: ref }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    localStorage.setItem('auth_token', data.access);
    return true;
  } catch {
    return false;
  }
}
